import {
  RecoSectionContent,
  TrackingPageTypeWithId,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk'
import CdnImage from 'components/CdnImage'
import { exists } from 'helpers/exists'
import { cdnImageUrl } from 'helpers/image'
import { ElementTrackingContextProvider } from 'helpers/tracking/ElementTrackingContext'
import { RecoSectionTrackingContextProvider } from 'helpers/tracking/SectionTrackingContext'
import { PromotionImpression } from 'helpers/tracking/trackerComponents/ElementTracker'
import { useObserver } from 'mobx-react-lite'
import React, { FC } from 'react'
import { Link } from 'react-router-dom'
import { useService } from 'services/utils/context'
import Brand from 'store/entities/Brands/Brand.store'
import styled from 'styled-components'
import { shade30, shade40 } from 'uiComponents/constants/colors'
import { fsMS } from 'uiComponents/constants/typography'
import { TeaserBackground } from './content-styles'

interface Props {
  brands: {
    id: string
    img: {
      backgroundBOHash: string
      logo: {
        w: number
        h: number
      }
    }
  }[]
}

interface TopBrandTileProps {
  brand: Brand
  tileWidth: number
  tileHeight: number
}

type BrandWithBackgroundImage = Brand & { backgroundImage?: string }

const BRAND_TILES_AMOUNT = 6

const TopBrandTile: FC<TopBrandTileProps> = ({
  brand,
  tileWidth,
  tileHeight,
}) => {
  if (!brand.transparentImageHash) {
    return <TopBrandTileTitle>{brand.name}</TopBrandTileTitle>
  }

  return (
    <CdnImage
      alt={brand.name}
      height={tileHeight}
      imageHash={brand.transparentImageHash}
      trim
      transparent
      width={tileWidth}
    />
  )
}

export const TopBrandsContentFocus: FC<Props> = ({ brands }) => {
  const service = useService()

  const filteredTopBrandRecommendations: BrandWithBackgroundImage[] =
    useObserver(() =>
      brands
        .map(brand => {
          const topBrand: BrandWithBackgroundImage | null =
            service.brands.getByBrandId(brand.id)

          if (exists(topBrand)) {
            topBrand.backgroundImage = brand.img.backgroundBOHash
          }

          return topBrand
        })
        .filter(exists)
        .filter(topBrand => exists(topBrand.logoImageHash))
        .slice(0, BRAND_TILES_AMOUNT),
    )

  return (
    <TopBrandsHolder data-testid="TopBrandsContentFocus">
      <RecoSectionTrackingContextProvider
        recoType={RecoSectionContent.topBrands}
        initiallyVisibleColumns={6}
      >
        {filteredTopBrandRecommendations.map(filteredTopBrandRecommendation => {
          const [brandId] = filteredTopBrandRecommendation.id.split('-')
          const rawBrand = brands.filter(brand => brand.id === brandId)[0]
          return (
            <ElementTrackingContextProvider
              key={filteredTopBrandRecommendation.id}
            >
              <PromotionImpression<HTMLDivElement>
                type={TrackingPageTypeWithId.Brand}
                id={brandId}
                name={filteredTopBrandRecommendation.name}
                url={filteredTopBrandRecommendation.url}
                source={TrackingPromotionCreative.BrandReco}
                useIdForDestination
              >
                {({ ref, onClickCapture }) => (
                  <TopBrandHolder>
                    <TeaserBackground
                      src={cdnImageUrl(
                        filteredTopBrandRecommendation.backgroundImage || '',
                        {
                          width: 300,
                          height: 300,
                        },
                      )}
                      alt={filteredTopBrandRecommendation.name || ''}
                      height={100}
                      width={100}
                    />
                    <div
                      onClickCapture={onClickCapture}
                      ref={ref}
                      key={filteredTopBrandRecommendation.id}
                    >
                      <TopBrandLink to={filteredTopBrandRecommendation.url}>
                        <TopBrandTile
                          brand={filteredTopBrandRecommendation}
                          tileWidth={rawBrand.img.logo.w}
                          tileHeight={rawBrand.img.logo.h}
                        />
                      </TopBrandLink>
                    </div>
                  </TopBrandHolder>
                )}
              </PromotionImpression>
            </ElementTrackingContextProvider>
          )
        })}
      </RecoSectionTrackingContextProvider>
    </TopBrandsHolder>
  )
}

const TopBrandsHolder = styled.div`
  margin-top: 1px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;
`

const TopBrandHolder = styled.div`
  position: relative;
  min-height: 150px;
  width: 100%;
  overflow: hidden;
  aspect-ratio: 1 / 1;
  &:not(:last-child) {
    margin-right: 1px;
  }
  > img {
    transform: scale(1);
    transition: transform 0.3s;
  }
  &:hover {
    > img {
      transform: scale(1.1);
    }
  }
`

const TopBrandLink = styled(Link)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${shade30};
  transition: 0.3s background-color;
  &:hover {
    background-color: ${shade40};
  }
`

const TopBrandTileTitle = styled.h5`
  color: white;
  font-weight: bold;
  font-size: ${fsMS}px;
  text-align: center;
`
